/**
 * Módulo 1 del tutorial: Crear tu campo (KAN-232).
 *
 * Guía la creación REAL del primer campo (unidad) de la empresa:
 * menú → Campos y Ubicaciones → Nuevo campo → form → guardar.
 *
 * Anclas: menu-ubicaciones (sidebar/header), campo-new-button,
 * campo-form-nombre, campo-form-hectareas, btn-save-campo (UnidadForm).
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

export const crearCampoModule: TutorialModuleDef = {
  id: 'crear-campo',
  title: 'Crear tu campo',
  description: 'Dale de alta tu campo en la app: nombre y hectáreas. Es la base para todo lo demás.',
  emoji: '🏞️',
  image: 'images/tutorial/crear-campo.png',
  permission: { module: 'locations', action: 'create' },
  grupo: 'esencial',
  route: '/ubicaciones',
  steps: [
    {
      id: 'bienvenida',
      route: '/tutorial',
      text: '¡Te damos la bienvenida a MiCampoDigital! Empecemos por lo primero: crear tu campo. Te voy guiando paso a paso.',
      narration:
        '¡Bienvenido a Mi Campo Digital, che! Antes de nada necesitamos conocer tu campo. No te preocupes que te voy a ir guiando paso a paso, como quien recorre el potrero de la mano. Primero vamos a crear tu campo: es la base de todo lo demás.',
    },
    {
      id: 'ir-a-campos',
      target: '[data-ar-guide="menu-ubicaciones"]',
      label: 'Campos y Ubicaciones',
      text: 'En el menú lateral, tocá "Campos y Ubicaciones" (en celular: abrí el menú ☰ arriba a la izquierda).',
      narration:
        'Mirá el menú del costado y buscá donde dice Campos y Ubicaciones. Si estás desde el celular, abrí el menú con las rayitas de arriba a la izquierda. Dale un toque ahí y seguimos.',
    },
    {
      id: 'nuevo-campo',
      route: '/ubicaciones',
      target: '[data-ar-guide="campo-new-button"]',
      label: 'Nuevo campo',
      text: 'Esta página organiza tus campos y sus ubicaciones. Tocá "Nuevo campo" para cargar el primero.',
      narration:
        'Esta página es donde vas a ver todos tus campos con sus ubicaciones, ordenaditos como alambre bien tensado. Por ahora está vacía, así que andá al botón Nuevo campo y tocálo para cargar el primero.',
    },
    {
      id: 'nombre',
      route: '/campos/new',
      target: '[data-ar-guide="campo-form-nombre"]',
      advanceOnClick: false,
      label: 'Nombre del campo',
      text: 'Ponele nombre a tu campo: como lo conocés vos (ej. "La Estancia", "Campo Norte").',
      narration:
        'Ahora ponele nombre a tu campo, como lo conocés vos de toda la vida. Si es La Estancia, Campo Norte, o como le diga la familia. Escribilo en este casillero.',
    },
    {
      id: 'hectareas',
      route: '/campos/new',
      target: '[data-ar-guide="campo-form-hectareas"]',
      advanceOnClick: false,
      label: 'Hectáreas',
      text: 'Si las sabés, cargá las hectáreas totales (es opcional — después podés completarlo desde el mapa).',
      narration:
        'Si tenés a mano las hectáreas del campo, anotalas acá. Y si no las sabés de memoria, no le des vueltas: dejalo vacío que después, con el mapa, las podés medir sin problema.',
    },
    {
      id: 'guardar',
      route: '/campos/new',
      target: '[data-ar-guide="btn-save-campo"]',
      label: 'Crear Campo',
      text: '¡Listo! Tocá "Crear Campo" para guardarlo.',
      narration:
        'Bueno, ya estamos. Apretá el botón Crear Campo y quedate tranquilo que queda guardado. Cuando lo toques, seguí con el próximo paso del tutorial.',
    },
  ],
  completionCheck: async () => {
    const unidades = await getRepositories().unidad.getAllForActiveEmpresa();
    return unidades.length > 0;
  },
};
